import { useActivityBuilderFlags } from 'approot/activity-builder/activity-builder-flags';
import { ViewProps } from './draw.constants';
import DrawCanvas from './canvas/draw-canvas';
import * as Styled from './draw-view.style';
import {
  useActivityAnswerId,
  useTeacherGetAnswers,
} from 'approot/activity-builder/present/activity-answers.hooks';
import { useBatchAnswersContext } from '../shared/batch-answers.context';
import { ACTIVITIES_ANSWER_DRAW_TYPE } from 'global/constants';
import type { DrawAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import ClassroomAnswerTabs, {
  INDIVIDUAL_VIEW,
  QUESTION_VIEW,
  SUGGESTED_VIEW,
} from 'approot/activity-builder/classroom/classroom-answer-tabs';
import ActivitySheet from '../shared/sheet/activity-sheet';
import DrawResponseIndividual from 'approot/activity-builder/classroom/draw/draw-response-individual';
import DrawResponseSuggestedAnswer from 'approot/activity-builder/classroom/draw/draw-response-suggested-answer';
import {
  ActivitySheetGridMax,
  ActivitySheetInnerContent,
  ActivitySheetInnerMax,
} from '../shared/sheet/activity-sheet.style';
import useInitialClassroomTab from 'approot/activity-builder/classroom/use-initial-classroom-tab';
import useDrawHasSuggestedAnswer from './use-draw-has-suggested-answer';
import ActivitySheetSaveStatus from '../shared/sheet/activity-sheet-save-status';

const DrawViewCheckAnswer = (
  props: ViewProps & {
    closeModal: () => void;
  }
) => {
  const {
    id,
    type,
    properties,
    subActivityProperties,
    suggestedAnswerProperties,
    closeModal,
  } = props;
  const { activityBuilderRespondFlag } = useActivityBuilderFlags();
  const { isPublished, lang } = useActivityBuilderPresentContext();
  const { tab, setTab } = useInitialClassroomTab({
    isVisible: true,
    canRespond: isPublished && activityBuilderRespondFlag,
    componentId: id,
  });

  const { componentAnswers, variables } = useTeacherGetAnswers({
    componentId: id,
    skip: !activityBuilderRespondFlag,
  });
  const answerData = componentAnswers?.at(0);
  const answerId = useActivityAnswerId(answerData);

  const { handleUpdate } = useBatchAnswersContext();

  const hasSuggestedAnswer = useDrawHasSuggestedAnswer({
    id,
    suggestedAnswerProperties,
  });

  return (
    <ActivitySheet
      isVisible
      title="Drawing response"
      tabsContent={
        <ClassroomAnswerTabs
          isPublished={!!isPublished}
          includeIndividualAnswers
          includeSuggestedAnswers={hasSuggestedAnswer}
          includeClassResponse={false}
          tab={tab}
          onSelect={option => setTab(option)}
          componentId={id}
          onCloseActivitySheet={() => {
            setTab(INDIVIDUAL_VIEW);
            closeModal();
          }}
        />
      }
      customActions={
        <ActivitySheetSaveStatus
          title="Drawing response"
          showStatus={tab === QUESTION_VIEW}
          onClose={() => {
            setTab(INDIVIDUAL_VIEW);
            closeModal();
          }}
        />
      }
    >
      {tab === QUESTION_VIEW && (
        <Styled.DrawViewTeacher id="content">
          <DrawCanvas
            id={id}
            type={type}
            imageUrl={properties.image?.original}
            dimensionsData={properties.dimensionData}
            answer={(answerData?.answer as DrawAnswerFragment)?.drawing}
            questionData={properties.draw || undefined}
            subActivityProperties={subActivityProperties}
            language={lang}
            onSave={content => {
              if (
                activityBuilderRespondFlag &&
                subActivityProperties?.activityComponentId &&
                variables
              ) {
                handleUpdate({
                  variables,
                  input: {
                    __typename: 'ActivityAnswer',
                    uuid: answerId,
                    answerType: ACTIVITIES_ANSWER_DRAW_TYPE,
                    activityId: subActivityProperties.activityComponentId,
                    componentId: id,
                    createdAt: null,
                    updatedAt: new Date(),
                    studentId: null,
                    answer: {
                      __typename: 'DrawAnswer',
                      answerType: ACTIVITIES_ANSWER_DRAW_TYPE,
                      drawing: content,
                      hasDrawingData: content.data.objects.length > 0,
                    },
                    answerReferences: null,
                  },
                });
              }
            }}
          />
        </Styled.DrawViewTeacher>
      )}
      {tab === INDIVIDUAL_VIEW && (
        <ActivitySheetInnerContent id="content">
          <ActivitySheetGridMax>
            <DrawResponseIndividual component={props} language={lang} />
          </ActivitySheetGridMax>
        </ActivitySheetInnerContent>
      )}
      {tab === SUGGESTED_VIEW && (
        <ActivitySheetInnerContent id="content">
          <ActivitySheetInnerMax>
            <DrawResponseSuggestedAnswer component={props} />
          </ActivitySheetInnerMax>
        </ActivitySheetInnerContent>
      )}
    </ActivitySheet>
  );
};

export default DrawViewCheckAnswer;
